import {
  Box,
  buildPath,
  buildTemplatePath,
  newToast,
  Page,
  PageRoute,
  SCHOOL_YEAR_ACTIVE_ID,
  Stack,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { ReportOutputFormat } from "components";
import { StaffFilter, StaffReportServiceDetailRequest, StaffReportServiceSummaryRequest } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { ServiceDetailRequest } from "./ServiceDetail/ServiceDetailRequest/ServiceDetailRequest";
import { ServiceDetailResponse } from "./ServiceDetail/ServiceDetailResponse";
import { ServiceSummaryRequest } from "./ServiceSummary/ServiceSummaryRequest/ServiceSummaryRequest";
import { ServiceSummaryResponse } from "./ServiceSummary/ServiceSummaryResponse";
import { StaffReportPageHeader } from "./StaffReportPageHeader";
import { StaffReportType } from "./StaffReportType";

const routeName = "staffReport";

export const staffReportPageRoute: PageRoute = {
  handle: "Staff",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type ReportType = "Services" | "Service Summary";
export type ScreenType = "config" | "report";

export type ReportSelection = {
  name: ReportType;
  generateTypes: ReportOutputFormat[];
};

const reportTypes: ReportSelection[] = [
  { name: "Services", generateTypes: ["on-screen", "pdf", "excel"] },
  { name: "Service Summary", generateTypes: ["on-screen", "pdf", "excel"] },
];

export const StaffReportPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedReportType, setSelectedReportType] = useState<ReportType | undefined>();
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [visitedReportTypes, setVisitedReportTypes] = useState<string[]>([]);
  const [screen, setScreen] = useState<ScreenType>("config");

  // this filter helps maintain filters for certain while toggling between reports
  //const [globalFilter, setGlobalFilter] = useState<StaffReportGlobalFilter>({});

  // report filters & results
  const [commonFilter, setCommonFilter] = useState<StaffFilter>({
    schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
  } as StaffFilter);

  const [serviceDetailRequest, setServiceDetailRequest] = useState<StaffReportServiceDetailRequest>({});
  const [serviceSummaryRequest, setServiceSummaryRequest] = useState<StaffReportServiceSummaryRequest>({});

  const [reportResponse, setReportResponse] = useState<any>();

  useEffect(() => {
    if (selectedReportType && !visitedReportTypes.includes(selectedReportType))
      setVisitedReportTypes((visitedReportTypes) => [...visitedReportTypes, selectedReportType]);
  }, [selectedReportType]);

  const generateReport = () => {
    setValidationErrors({});

    switch (selectedReportType) {
      case "Services":
        ApiService.staffReports
          .generateServiceDetail({ ...serviceDetailRequest, staffFilter: commonFilter })
          .then((response) => {
            setScreen("report");
            setReportResponse(response);
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            setValidationErrors(ex.response);
          });
        break;
      case "Service Summary":
        ApiService.staffReports
          .generateServiceSummary({ ...serviceSummaryRequest, staffFilter: commonFilter })
          .then((response) => {
            setScreen("report");
            setReportResponse(response);
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            setValidationErrors(ex.response);
          });
        break;
    }
  };

  return (
    <Page
      isForm
      header={
        <StaffReportPageHeader
          handle={staffReportPageRoute.handle}
          reportTypes={reportTypes}
          selectedReportType={selectedReportType}
          screen={screen}
          setScreen={setScreen}
          generateReport={generateReport}
        />
      }
    >
      <Box show={screen === "config"}>
        <Stack spacing="lg">
          <StaffReportType
            reportTypes={reportTypes}
            selectedReportType={selectedReportType}
            setSelectedReportType={setSelectedReportType}
          />

          <Box show={selectedReportType === "Services"}>
            <ServiceDetailRequest
              initialized={visitedReportTypes.includes("Services")}
              request={serviceDetailRequest}
              setRequest={setServiceDetailRequest}
              globalCommonFilter={commonFilter}
              setGlobalCommonFilter={setCommonFilter}
              validationErrors={validationErrors}
            />
          </Box>

          <Box show={selectedReportType === "Service Summary"}>
            <ServiceSummaryRequest
              initialized={visitedReportTypes.includes("Service Summary")}
              request={serviceSummaryRequest}
              setRequest={setServiceSummaryRequest}
              globalCommonFilter={commonFilter}
              setGlobalCommonFilter={setCommonFilter}
              validationErrors={validationErrors}
            />
          </Box>
        </Stack>
      </Box>

      {screen === "report" && (
        <Box>
          {selectedReportType === "Services" && <ServiceDetailResponse response={reportResponse} />}
          {selectedReportType === "Service Summary" && <ServiceSummaryResponse response={reportResponse} />}
        </Box>
      )}
    </Page>
  );
};
